import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  PaginatedResponseInterface,
  SportInterface,
  SportsGroupFilterParamInterface,
  SportsGroupInterface,
} from '@sport-activities/sdk';
import { Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { APP_STORAGE } from '~/app.token';

import { createHttpParams } from '~/core/helpers/create-http-params/create-http-params.helper';
import { Logger } from '~/core/logger.service';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';
import { UnleashService } from '~/toggles/services/unleash.service';

import { environment } from '~environments/environment';

const SPORTS_GROUPS = 'sportsGroups';
const SPORTS_LIST = 'sportsList';
const SPORTS_LIST_DATE = 'sportsListDate';

@Injectable({
  providedIn: 'root',
})
export class SportsService {
  protected sportsUrl: string;
  public sports: SportInterface[] = [];
  public sportsGroups: SportsGroupInterface[] = [];

  constructor(
    @Inject(APP_STORAGE) private readonly storage: Storage,
    private readonly httpClient: HttpClient,
    private readonly toggleService: ToggleService,
    private readonly unleashService: UnleashService,
    protected readonly logger: Logger
  ) {}

  private getSports(): Observable<SportInterface[]> {
    return this.httpClient.get<SportInterface[]>(`${this.sportsUrl}`);
  }

  public getSport(id: number): SportInterface | undefined {
    return this.sports.find((sport) => sport.id === id);
  }

  public getSportsGroup(
    sportGroupId: number
  ): Observable<SportsGroupInterface> {
    return this.httpClient
      .get<
        PaginatedResponseInterface<SportsGroupInterface>
      >(`${environment.applications.activities.url}/sports-groups?identifiers[]=${sportGroupId}`, {})
      .pipe(
        map((paginatedResponse) => {
          if (paginatedResponse.items.length !== 1) {
            this.logger.error(
              // eslint-disable-next-line max-len
              `SportsService::getSportsGroup() - ${paginatedResponse.items.length} returned instead of 1 for sport group id ${sportGroupId}.`
            );
          }
          return paginatedResponse.items[0];
        })
      );
  }

  public getSportsGroups(
    filters?: SportsGroupFilterParamInterface
  ): Observable<SportsGroupInterface[]> {
    const defaultPageSize = 300;
    const sportsGroupsStorageData = this.storage?.getItem(SPORTS_GROUPS);

    if (
      !filters &&
      sportsGroupsStorageData &&
      !this.timeToRefreshSportsData()
    ) {
      this.sportsGroups = JSON.parse(sportsGroupsStorageData);

      return of(this.sportsGroups);
    } else {
      this.storage?.removeItem(SPORTS_GROUPS);
      return this.httpClient
        .get<PaginatedResponseInterface<SportsGroupInterface>>(
          `${environment.applications.activities.url}/sports-groups?pagination[limit]=${defaultPageSize}`,
          {
            params: createHttpParams(filters),
          }
        )
        .pipe(
          map((paginatedResponse) => {
            if (paginatedResponse.total > defaultPageSize) {
              this.logger.warn(
                // eslint-disable-next-line max-len
                `SportsService::getSportsGroups() - More than ${defaultPageSize} results from Api Call. Received ${paginatedResponse.total} results.`
              );
            }

            const excludedSports = this.toggleService
              .getVariantString(ToggleNameEnum.ExcludedSportsGroups, '')
              .split(',')
              .map((id) => +id);

            this.sportsGroups = paginatedResponse.items.filter(
              ({ id }) => !excludedSports.includes(id)
            );

            this.storage.setItem(
              SPORTS_GROUPS,
              JSON.stringify(this.sportsGroups)
            );

            return this.sportsGroups;
          })
        );
    }
  }

  public initialize(baseUrl: string): Observable<SportInterface[]> {
    this.sportsUrl = `${baseUrl}/sports`;

    const sportsStorageData = this.storage?.getItem(SPORTS_LIST);

    if (sportsStorageData && !this.timeToRefreshSportsData()) {
      this.sports = JSON.parse(sportsStorageData);

      return of(this.sports);
    } else {
      this.storage?.removeItem(SPORTS_LIST);
      this.storage?.removeItem(SPORTS_LIST_DATE);
      this.getSports()
        .pipe(
          mergeMap((sports) => {
            return this.unleashService.isInitialized().pipe(
              mergeMap((initialized) => {
                let excludedSports = [];
                if (initialized) {
                  excludedSports = this.toggleService
                    .getVariantString(ToggleNameEnum.ExcludedSports, '')
                    .split(',');
                }

                return of({ sports, excludedSports });
              })
            );
          })
        )
        .subscribe(({ sports, excludedSports }) => {
          this.sports = sports.filter(
            (s) => s.name && !excludedSports.includes(s.id.toString())
          );

          this.storage.setItem(SPORTS_LIST, JSON.stringify(this.sports));

          this.storage?.setItem(
            SPORTS_LIST_DATE,
            JSON.stringify(new Date().getTime())
          );
        });

      return of(this.sports);
    }
  }

  private timeToRefreshSportsData(): boolean {
    const sportsStorageDataDate = JSON.parse(
      this.storage?.getItem(SPORTS_LIST_DATE)
    );
    const refreshDelay =
      Number(
        this.toggleService.getVariant(ToggleNameEnum.SportsListStorage, '24')
      ) || 24;

    return (
      new Date().getTime() - sportsStorageDataDate >
      refreshDelay * 60 * 60 * 1000
    );
  }
}
